import React from 'react';
import { Box, Button, Stack, Wrap, For } from '@chakra-ui/react';
import {
  SwitchOnOff,
  Input,
  NumberInput,
  SelectWithAction,
  CheckBoxGroup,
  ComboBox,
  Tooltip,
  labelStyles,
} from '../../ui';
import { useATFilterAdvanced, useATColumns } from '../hooks';
import { ATFilterBarProps } from './ATTypes';
import { LuFilterX } from 'react-icons/lu';

import {
  initialiseFilterConfig,
  handleChangeInput,
  handleChangeNumber,
  handleChangeSwitch,
  type FilterConfigComponentType,
  handleChangeSelect,
} from '../filter/FilterLogic';

export function ActionTableFilterBar<T extends Record<string, any>>({
  filterConfigComponents = [],
  disabled = false,
  extraFilterComponents = [],
  extraActionComponents = [],
  resetButtonProps = {},
  ...rest
}: ATFilterBarProps<T>) {
  // Filterable
  const { columns } = useATColumns();
  const { setFiltersAdvanced } = useATFilterAdvanced();

  const [initializedFilterConfig, setInitializedFilterConfig] = React.useState<
    FilterConfigComponentType<T>[]
  >([]);
  const [triggerReset, setTriggerReset] = React.useState(0);

  React.useEffect(() => {
    (() => {
      const { tempFilterItems, tempFilterConfigComponents } =
        initialiseFilterConfig<T>(filterConfigComponents, columns);
      setInitializedFilterConfig(tempFilterConfigComponents);
      setFiltersAdvanced(tempFilterItems);
    })();
  }, [filterConfigComponents]);

  return (
    <Box
      width={'100%'}
      display="flex"
      flexDirection={{ base: 'column', md: 'row' }}
      gap={{ base: 2, md: 4 }}
      alignItems={{ base: 'flex-end', md: 'center' }}
      justifyContent="center"
    >
      <Wrap
        flex="auto"
        minWidth={0}
        alignItems="center"
        justifyContent="center"
        gap={5}
        p={5}
      >
        {initializedFilterConfig.map((item, componentIndex) => {
          const renderFilterComponent = () => {
            // Handle different filter types
            switch (item.filterComponent) {
              case 'date':
              case 'input': {
                return (
                  <Box
                    key={`container-${item.componentKey}`}
                    width={'100%'}
                    minWidth={'300px'}
                    maxWidth={'400px'}
                    {...item.containerProps}
                  >
                    <Input
                      key={`${item.componentKey}`}
                      width={'100%'}
                      minWidth={'300px'}
                      maxWidth={'400px'}
                      placeholder={`Filter by ${item.componentLabel}...`}
                      label={item.componentLabel}
                      defaultValue={String(
                        item.componentDefaultValue as string,
                      )}
                      disabled={disabled}
                      onChange={(value: string) => {
                        handleChangeInput(
                          value,
                          item.componentValue?.itemKey,
                          item.componentValue?.item?.colKeys,
                          item.componentValue?.item?.operator,
                          setFiltersAdvanced,
                          item.filterComponent,
                        );
                      }}
                      triggerResetToDefault={triggerReset}
                      {...(item.filterComponent === 'date'
                        ? { type: 'date' }
                        : {})}
                      {...item.filterComponentProps}
                      {...rest}
                    />
                  </Box>
                );
              }

              case 'number': {
                return (
                  <Box
                    width={'100%'}
                    minWidth={'300px'}
                    maxWidth={'400px'}
                    key={`container-${item.componentKey}`}
                    {...item.containerProps}
                  >
                    <NumberInput
                      key={`${item.componentKey}`}
                      width={'100%'}
                      minWidth={'300px'}
                      maxWidth={'400px'}
                      placeholder={`Filter by ${item.componentLabel}...`}
                      label={item.componentLabel}
                      defaultValue={String(
                        item.componentDefaultValue as number,
                      )}
                      disabled={disabled}
                      onChange={(value: number) => {
                        handleChangeNumber(
                          value,
                          item.componentValue?.itemKey,
                          item.componentValue?.item?.colKeys,
                          item.componentValue?.item?.operator,
                          setFiltersAdvanced,
                        );
                      }}
                      triggerResetToDefault={triggerReset}
                      {...item.filterComponentProps}
                      {...rest}
                    />
                  </Box>
                );
              }

              case 'switch': {
                return (
                  <Box
                    width="fit-content"
                    maxWidth="100%"
                    alignItems="center"
                    justifySelf="center"
                    key={`container-${item.componentKey}`}
                    {...item.containerProps}
                  >
                    <SwitchOnOff
                      key={`${item.componentKey}`}
                      disabled={disabled}
                      defaultValue={
                        item.componentValue?.item?.autoFilter ?? false
                      }
                      onValueChange={(checked: boolean) => {
                        handleChangeSwitch(
                          checked,
                          item.componentValue?.itemKey,
                          item.componentValue?.item?.colKeys,
                          item.componentValue?.item?.operator,
                          setFiltersAdvanced,
                          item.componentValue?.item?.value,
                        );
                      }}
                      triggerResetToDefault={triggerReset}
                      label={item.componentLabel}
                      {...item.filterComponentProps}
                    >
                      {item.componentValue?.item?.label || item.componentLabel}
                    </SwitchOnOff>
                  </Box>
                );
              }

              case 'combobox': {
                return (
                  <Box
                    width={'100%'}
                    minWidth={'300px'}
                    maxWidth={'400px'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    key={`container-${item.componentKey}`}
                    {...item.containerProps}
                  >
                    <ComboBox
                      key={`${item.componentKey}`}
                      disabled={disabled}
                      items={item.componentValue || []}
                      defaultValue={
                        (item.componentDefaultValue as string[]) || []
                      }
                      keyForDisplay={'label'}
                      keyForValue={'value'}
                      multiple={true}
                      getEntireItem={true}
                      label={item.componentLabel}
                      placeholder={`Filter by ${item.componentLabel}`}
                      onSelect={(value: any) => {
                        handleChangeSelect(
                          value.items || [],
                          setFiltersAdvanced,
                          componentIndex,
                          item.filterComponentPredicate || 'or',
                          item.defaultColKeys,
                          columns,
                        );
                      }}
                      useSelectAllOption={item.isSelectAllOption}
                      filterComponent="contains"
                      triggerResetToDefault={triggerReset}
                      {...item.filterComponentProps}
                    />
                  </Box>
                );
              }

              case 'select': {
                // Use pre-normalized values from initialization

                return (
                  <Box
                    width={'100%'}
                    minWidth={'300px'}
                    maxWidth={'400px'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    key={`container-${item.componentKey}`}
                    {...item.containerProps}
                  >
                    <SelectWithAction
                      key={`${item.componentKey}`}
                      disabled={disabled}
                      items={item.componentValue || []}
                      defaultValue={
                        (item.componentDefaultValue as string[]) || []
                      }
                      keyForDisplay={'label'}
                      keyForValue={'value'}
                      multiple={true}
                      label={item.componentLabel}
                      placeholder={`Filter by ${item.componentLabel}...`}
                      getEntireItem={true}
                      onSelect={(value: any) => {
                        handleChangeSelect(
                          value.items || [],
                          setFiltersAdvanced,
                          componentIndex,
                          item.filterComponentPredicate || 'or',
                          item.defaultColKeys,
                          columns,
                        );
                      }}
                      useSelectAllOption={item.isSelectAllOption}
                      triggerResetToDefault={triggerReset}
                      {...item.filterComponentProps}
                    />
                  </Box>
                );
              }

              case 'checkbox': {
                // Use pre-normalized values from initialization

                return (
                  <Box
                    width="fit-content"
                    maxWidth="100%"
                    alignItems="center"
                    justifySelf="center"
                    key={`container-${item.componentKey}`}
                    {...item.containerProps}
                  >
                    <CheckBoxGroup
                      key={`${item.componentKey}`}
                      disabled={disabled}
                      items={item.componentValue || []}
                      defaultValue={
                        (item.componentDefaultValue as string[]) || []
                      }
                      keyForDisplay={'label'}
                      keyForValue={'value'}
                      multiple={true}
                      getEntireItem={true}
                      label={item.componentLabel}
                      onSelect={(value: any) => {
                        handleChangeSelect(
                          value.items || [],
                          setFiltersAdvanced,
                          componentIndex,
                          item.filterComponentPredicate || 'or',
                          item.defaultColKeys,
                          columns,
                        );
                      }}
                      useSelectAllOption={item.isSelectAllOption}
                      triggerResetToDefault={triggerReset}
                      {...item.filterComponentProps}
                    />
                  </Box>
                );
              }

              default:
                return null;
            }
          };

          return renderFilterComponent();
        })}
        {extraFilterComponents.map((component, index) => (
          <Box key={`extra-filter-component-${index}`}>{component}</Box>
        ))}
      </Wrap>
      <Wrap flex="1" gap={3} alignItems="center" justifyContent="center">
        <Button
          disabled={disabled}
          variant="outline"
          size="xs"
          fontSize="xs"
          onClick={() => {
            setTriggerReset((value) => value + 1);
          }}
          gap={ 2 }
          colorPalette="primary"
          {...resetButtonProps}
        >
          <LuFilterX />
          Reset Filters
        </Button>

        {extraActionComponents.map((component, index) => (
          <Box key={`extra-action-component-${index}`}>{component}</Box>
        ))}
      </Wrap>
    </Box>
  );
}
